import type { IExercise } from '../exercises';
import { REVIEWED_LIBRARY_IDENTITIES } from './reviewed-library-identities';

const SUBCATEGORY_ORDER: Record<string, string[]> = {
  chest: ['upper-chest', 'mid-chest', 'lower-chest', 'inner-chest'],
  back: ['width', 'thickness'],
  shoulders: ['front-deltoid', 'side-deltoid', 'rear-deltoid'],
  arms: ['biceps', 'triceps'],
  legs: ['quadriceps', 'hamstrings', 'glutes', 'calves', 'inner-thigh', 'hip-flexor'],
  abs: ['upper-abs', 'lower-abs', 'obliques', '综合'],
  warmup: ['full-body', 'general', 'chest', 'back', 'shoulder', 'arms', 'hip-leg', 'thoracic', 'neck'],
  stretch: ['chest-stretch', 'back-stretch', 'shoulder-stretch', 'arm-stretch', 'leg-stretch', 'abs-stretch', 'neck'],
};
const MODULE_ORDER = Object.keys(SUBCATEGORY_ORDER);

/** Each standard is independent of the immutable teaching instances and historical records. */
export function buildCanonicalExerciseLibrary(materials: IExercise[]): IExercise[] {
  const byId = new Map(materials.map(exercise => [exercise.id, exercise]));
  return REVIEWED_LIBRARY_IDENTITIES.map(identity => {
    const original = byId.get(identity.legacyIds[0]);
    const teaching = byId.get(identity.sourceId);
    if (!original || !teaching) throw new Error(`Missing reviewed library source: ${identity.id}`);
    const originals = identity.legacyIds.map(id => byId.get(id)!);
    const aliases = [...new Set(originals.map(exercise => exercise.name))]
      .filter(name => identity.name !== name &&
        !(identity.id === 'library-cable-face-pull' && name === '颈后臂屈伸'));
    return {
      ...original,
      id: identity.id,
      name: identity.name,
      bodyPart: identity.group,
      subCategory: identity.group === 'abs' && identity.subCategory === 'core' ? '综合' : identity.subCategory,
      equipment: identity.equipment,
      targetMuscles: identity.targetMuscles,
      isBodyweight: identity.isBodyweight ?? identity.equipment === 'bodyweight',
      isWarmup: identity.group === 'warmup',
      isStretch: identity.group === 'stretch',
      libraryGroup: identity.group,
      movementPattern: identity.pattern,
      aliases,
      tips: teaching.tips,
      detailGuide: teaching.detailGuide,
      source: teaching.source || original.source,
      // The reviewed source owns all media roles; a missing role never falls back to an old mismatch.
      demoUrl: teaching.demoUrl,
      videoUrl: teaching.videoUrl,
      demoStatus: teaching.demoUrl ? 'ready' : 'pending',
      mediaIssue: teaching.mediaIssue,
      premium: originals.every(exercise => !!exercise.premium),
    };
  }).sort((a, b) => MODULE_ORDER.indexOf(a.libraryGroup) - MODULE_ORDER.indexOf(b.libraryGroup) ||
    SUBCATEGORY_ORDER[a.libraryGroup].indexOf(a.subCategory) - SUBCATEGORY_ORDER[b.libraryGroup].indexOf(b.subCategory));
}
